/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Row, Col, notification, Table, Card } from 'antd';
import { getDashboardExchangeData } from '../../../../services/dashboard-services';
import Loader from '../../../../components/common/Loader';
import ExchangeFilters from './filters/ExchangeFilters';
import { useDispatch, useSelector } from 'react-redux';
import { saveDashboardHomeFilters } from '../../../auth/slice/authSlice';
import NoDataFound from '../../../../components/common/NoDataFound';

const DashboardExchange = () => {
  const dispatch = useDispatch();
  const roleType = useSelector((state) => state?.auth?.userData?.role);
  const [exchangeData, setExchangeData] = useState({
    area_table: [],
    model_table: [],
    exchange_team_table: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    model_id: '',
    source_id: '',
    area_id: '',
    start_date: '',
    end_date: '',
  });

  // Helper: leads base url based on role (align to DefaultLayout)
  const getLeadsBaseUrl = () => {
    if (roleType === 'Super Admin') return '/admin/leads';
    if (roleType === 'Exchange Evaluation Team' || roleType === 'Procurement Manager') return '/leads/exchange-lead';
    return '/leads';
  };

  // Helper: append dashboard redirect and dates
  const getRedirectUrl = (baseUrl) => {
    let url = baseUrl;
    // Only append simple params if array-style not already present in baseUrl
    if (filters?.model_id && !/model_type\[\d+\]=/.test(baseUrl)) {
      url += `&model_type=${encodeURIComponent(filters.model_id)}`;
    }
    if (filters?.source_id && !/lead_source\[\d+\]=/.test(baseUrl)) {
      // General leads page expects 'lead_source' (singular) which maps into filters.lead_sources later
      url += `&lead_source=${encodeURIComponent(filters.source_id)}`;
    }
    if (filters?.area_id && !/area_id\[\d+\]=/.test(baseUrl)) {
      url += `&area_id=${encodeURIComponent(filters.area_id)}`;
    }
    url += `&dashboard_redirect=true&start_date=${filters?.start_date || ''}&end_date=${filters?.end_date || ''}`;
    return url;
  };


  useEffect(() => {
    if (filters?.model_id || filters?.source_id || filters?.area_id || filters?.start_date || filters?.end_date) {
      getDashboardExchangeDetails();
    }
    dispatch(saveDashboardHomeFilters(filters));
  }, [filters]);

  //Api call to get dashboard exchange data
  const getDashboardExchangeDetails = async () => {
    setIsLoading(true);
    try {
      const reqData = {
        area_id: filters?.area_id || '',
        model_id: filters?.model_id || '',
        source_id: filters?.source_id || '',
        start_date: filters?.start_date || '',
        end_date: filters?.end_date || '',
      };
      const response = await getDashboardExchangeData(reqData);
      setExchangeData(response && response?.data && response?.data?.data ? response.data.data : {
        area_table: [],
        model_table: [],
        exchange_team_table: []
      });
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Area Table Columns
  const areaTableColumns = [
    {
      title: 'Area',
      dataIndex: 'area_name',
      key: 'area_name',
      render: (text, record) => (
        <span className={record.area_name === 'Total' ? 'font-weight-bold' : ''}>
          {text}
        </span>
      ),
    },
    {
      title: 'Pending For Evaluation Previous Months',
      dataIndex: 'pending_for_evaluation_prev_month',
      key: 'pending_for_evaluation_prev_month',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.area_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&area_id[0]=${record?.area_id}&lead_exchange_stage_id=1&lead_exchange_stage_name=${encodeURIComponent('Pending for Evaluation')}&last_month="true"`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Pending for Evaluation (This Month)',
      dataIndex: 'pending_for_evaluation_current_month',
      key: 'pending_for_evaluation_current_month',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.area_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&area_id[0]=${record?.area_id}&lead_exchange_stage_id=1&lead_exchange_stage_name=${encodeURIComponent('Pending for Evaluation(Current Month)')}&current_month=true`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Pre Evaluation',
      dataIndex: 'pre_evaluation',
      key: 'pre_evaluation',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.area_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&area_id[0]=${record?.area_id}&lead_exchange_stage_id=2&lead_exchange_stage_name=${encodeURIComponent('Pre Evaluation')}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Re Evaluation',
      dataIndex: 're_evaluation',
      key: 're_evaluation',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.area_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&area_id[0]=${record?.area_id}&lead_exchange_stage_id=4&lead_exchange_stage_name=${encodeURIComponent('Re Evaluation')}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Evaluation Completed',
      dataIndex: 'evaluation_completed',
      key: 'evaluation_completed',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.area_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&area_id[0]=${record?.area_id}&lead_exchange_stage_id=5&lead_exchange_stage_name=${encodeURIComponent('Evaluation Completed')}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Pre Final Assessment',
      dataIndex: 'pre_final_assessment',
      key: 'pre_final_assessment',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.area_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&area_id[0]=${record?.area_id}&lead_exchange_stage_id=6&lead_exchange_stage_name=${encodeURIComponent('Pre Final Assessment')}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Final Assessment Completed',
      dataIndex: 'final_assessment_completed',
      key: 'final_assessment_completed',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.area_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&area_id[0]=${record?.area_id}&lead_exchange_stage_id=7&lead_exchange_stage_name=${encodeURIComponent('Final Assessment Completed')}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Procured',
      dataIndex: 'procured',
      key: 'procured',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.area_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&area_id[0]=${record?.area_id}&procured=true`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
  ];

  // Model Table Columns
  const modelTableColumns = [
    {
      title: 'Model',
      dataIndex: 'model_name',
      key: 'model_name',
      render: (text, record) => (
        <span className={record.model_name === 'Total' ? 'font-weight-bold' : ''}>
          {text}
        </span>
      ),
    },
    {
      title: 'Pending For Evaluation Previous Months',
      dataIndex: 'pending_for_evaluation_prev_month',
      key: 'pending_for_evaluation_prev_month',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.model_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&model_type[0]=${record?.model_id}&lead_exchange_stage_id=1&lead_exchange_stage_name=${encodeURIComponent('Pending for Evaluation')}&last_month="true"`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Pending for Evaluation (This Month)',
      dataIndex: 'pending_for_evaluation_current_month',
      key: 'pending_for_evaluation_current_month',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.model_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&model_type[0]=${record?.model_id}&lead_exchange_stage_id=1&lead_exchange_stage_name=${encodeURIComponent('Pending for Evaluation (current Month)')}&current_month=true`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Pre Evaluation',
      dataIndex: 'pre_evaluation',
      key: 'pre_evaluation',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.model_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&model_type[0]=${record?.model_id}&lead_exchange_stage_id=2&lead_exchange_stage_name=${encodeURIComponent('Pre Evaluation')}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Re Evaluation',
      dataIndex: 're_evaluation',
      key: 're_evaluation',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.model_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&model_type[0]=${record?.model_id}&lead_exchange_stage_id=4&lead_exchange_stage_name=${encodeURIComponent('Re Evaluation')}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Evaluation Completed',
      dataIndex: 'evaluation_completed',
      key: 'evaluation_completed',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.model_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&model_type[0]=${record?.model_id}&lead_exchange_stage_id=5&lead_exchange_stage_name=${encodeURIComponent('Evaluation Completed')}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Pre Final Assessment',
      dataIndex: 'pre_final_assessment',
      key: 'pre_final_assessment',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.model_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&model_type[0]=${record?.model_id}&lead_exchange_stage_id=6&lead_exchange_stage_name=${encodeURIComponent('Pre Final Assessment')}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Final Assessment Completed',
      dataIndex: 'final_assessment_completed',
      key: 'final_assessment_completed',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.model_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&model_type[0]=${record?.model_id}&lead_exchange_stage_id=7&lead_exchange_stage_name=${encodeURIComponent('Final Assessment Completed')}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Procured',
      dataIndex: 'procured',
      key: 'procured',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.model_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&model_type[0]=${record?.model_id}&procured=true`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
  ];

  // Exchange Team Table Columns
  const exchangeTeamTableColumns = [
    {
      title: 'Exchange Evaluation Team',
      dataIndex: 'user_name',
      key: 'user_name',
      render: (text, record) => (
        <span className={record.user_name === 'Total' ? 'font-weight-bold' : ''}>
          {text}
        </span>
      ),
    },
    {
      title: 'Pending For Evaluation Previous Months',
      dataIndex: 'pending_for_evaluation_prev_month',
      key: 'pending_for_evaluation_prev_month',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.user_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&exchange_officer_id=${record?.user_id}&lead_exchange_stage_id=1&lead_exchange_stage_name=${encodeURIComponent('Pending for Evaluation')}&last_month="true"`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Pending for Evaluation (This Month)',
      dataIndex: 'pending_for_evaluation_current_month',
      key: 'pending_for_evaluation_current_month',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.user_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&exchange_officer_id=${record?.user_id}&exchange_officer_name=${encodeURIComponent(record?.user_name)}&lead_exchange_stage_id=1&lead_exchange_stage_name=${encodeURIComponent('Pending for Evaluation (Current Month)')}&current_month=true`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Pre Evaluation',
      dataIndex: 'pre_evaluation',
      key: 'pre_evaluation',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.user_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&exchange_officer_id=${record?.user_id}&exchange_officer_name=${encodeURIComponent(record?.user_name)}&lead_exchange_stage_id=2&lead_exchange_stage_name=${encodeURIComponent('Pre Evaluation')}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Re Evaluation',
      dataIndex: 're_evaluation',
      key: 're_evaluation',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.user_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&exchange_officer_id=${record?.user_id}&exchange_officer_name=${encodeURIComponent(record?.user_name)}&lead_exchange_stage_id=4&lead_exchange_stage_name=${encodeURIComponent('Re Evaluation')}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Evaluation Completed',
      dataIndex: 'evaluation_completed',
      key: 'evaluation_completed',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.user_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&exchange_officer_id=${record?.user_id}&lead_exchange_stage_id=5&lead_exchange_stage_name=${encodeURIComponent('Evaluation Completed')}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Pre Final Assessment',
      dataIndex: 'pre_final_assessment',
      key: 'pre_final_assessment',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.user_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&exchange_officer_id=${record?.user_id}&exchange_officer_name=${encodeURIComponent(record?.user_name)}&lead_exchange_stage_id=6&lead_exchange_stage_name=${encodeURIComponent('Pre Final Assessment')}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Final Assessment Completed',
      dataIndex: 'final_assessment_completed',
      key: 'final_assessment_completed',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.user_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&exchange_officer_id=${record?.user_id}&exchange_officer_name=${encodeURIComponent(record?.user_name)}&lead_exchange_stage_id=7&lead_exchange_stage_name=${encodeURIComponent('Final Assessment Completed')}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Procured',
      dataIndex: 'procured',
      key: 'procured',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.user_name === 'Total' ? (
            <span className="font-weight-bold">{text}</span>
          ) : (
            <>
              {+text !== 0 ? (
                <a
                  href={getRedirectUrl(`${getLeadsBaseUrl()}?key=hot_lead&exchange_officer_id=${record?.user_id}&exchange_officer_name=${encodeURIComponent(record?.user_name)}&procured=true`)}
                  style={{ cursor: 'pointer' }}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Evaluation Ratio',
      dataIndex: 'evaluation_ratio',
      key: 'evaluation_ratio',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.user_name === 'Total' ? (
            <span className="font-weight-bold">{text}%</span>
          ) : (
            <span>{text}%</span>
          )}
        </>
      ),
    },
    {
      title: 'Procurement Ratio',
      dataIndex: 'procurement_ratio',
      key: 'procurement_ratio',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.user_name === 'Total' ? (
            <span className="font-weight-bold">{text}%</span>
          ) : (
            <span>{text}%</span>
          )}
        </>
      ),
    },
    {
      title: 'TAT Adherence',
      dataIndex: 'tat_adherence',
      key: 'tat_adherence',
      className: 'text-center',
      render: (text, record) => (
        <>
          {record.user_name === 'Total' ? (
            <span className="font-weight-bold">{text}%</span>
          ) : (
            <span>{text}%</span>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <Loader loading={isLoading} />
      <div className='px-0'>
        <ExchangeFilters onFiltersChange={setFilters} />

        <Row gutter={[20, 20]}>
          {/* Area Table */}
          <Col xl={24} lg={24} md={24} sm={24} xs={24} className="mb-4">
            <Card>
              <div className='user-table-data'>
                <div className='user-left'>
                  <h5 className='pb-3'>Area Table</h5>
                </div>
                <div className='responsive-table-container'>
                  {exchangeData?.area_table && exchangeData.area_table.length > 0 ? (
                    <Table
                      columns={areaTableColumns}
                      dataSource={exchangeData.area_table}
                      pagination={false}
                      rowClassName={(record) => record.area_name === 'Total' ? 'total-row' : ''}
                      bordered
                    />
                  ) : (
                    <NoDataFound />
                  )}
                </div>
              </div>
            </Card>
          </Col>

          {/* Model Table */}
          <Col xl={24} lg={24} md={24} sm={24} xs={24} className="mb-4">
            <Card>
              <div className='user-table-data'>
                <div className='user-left'>
                  <h5 className='pb-3'>Model Table</h5>
                </div>
                <div className='responsive-table-container'>
                  {exchangeData?.model_table && exchangeData.model_table.length > 0 ? (
                    <Table
                      columns={modelTableColumns}
                      dataSource={exchangeData.model_table}
                      pagination={false}
                      rowClassName={(record) => record.model_name === 'Total' ? 'total-row' : ''}
                      bordered
                    />
                  ) : (
                    <NoDataFound />
                  )}
                </div>
              </div>
            </Card>
          </Col>

          {/* Exchange Team Table */}
          <Col xl={24} lg={24} md={24} sm={24} xs={24} className="mb-4">
            <Card>
              <div className='user-table-data'>
                <div className='user-left'>
                  <h5 className='pb-3'>Exchange Evaluation Team Table</h5>
                </div>
                <div className='responsive-table-container'>
                  {exchangeData?.exchange_team_table && exchangeData.exchange_team_table.length > 0 ? (
                    <Table
                      columns={exchangeTeamTableColumns}
                      dataSource={exchangeData.exchange_team_table}
                      pagination={false}
                      rowClassName={(record) => record.user_name === 'Total' ? 'total-row' : ''}
                      bordered
                    />
                  ) : (
                    <NoDataFound />
                  )}
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default DashboardExchange;
